/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { Minimize2 } from "lucide-react";
import Gemini from "../../icons/gemini-ai.svg?react";
import PulseLoader from "react-spinners/PulseLoader";
import { PiHandWavingFill } from "react-icons/pi";

interface ConversationMessage {
  name: string;
  keyPoints: string[];
  contextLabel: string;
}

interface AICompanionProps {
  onClose: () => void;
  aiSuggestion: any;
  aiContextHistory: any;
  loading?: boolean;
  userData?: any;
}

const formatContextLabel = (contextName: string, value: string): string => {
  if (!contextName) return value;

  switch (contextName.toLowerCase()) {
    case "organizations":
      return `Organization: ${value}`;
    case "applications":
      return `Application: ${value}`;
    case "auditee_member":
      return `Auditee Member: ${value}`;
    case "auditor":
    case "auditor_member":
      return `Auditor: ${value}`;
    case "theme":
      return `Theme: ${value}`;
    case "audit_level":
      return `Audit Level: ${value}`;
    case "duration":
      return `Duration: ${value}`;
    case "response_time":
      return `Response Time: ${value}`;
    case "audit_mode":
      return `Audit Mode: ${value}`;
    case "reminder_alert":
      return `Reminder Alert: ${value}`;
    default:
      return `${contextName.charAt(0).toUpperCase() + contextName.slice(1)}: ${value}`;
  }
};

const formatPlainTextToHtml = (text: string): string => {
  if (!text || text.trim().startsWith("<")) return text;

  const lines = text.split("\n").filter((line) => line.trim());
  let html = "";
  let inList = false;

  for (const line of lines) {
    const trimmed = line.trim();

    if (/^[•●▪-]\s*/.test(trimmed)) {
      if (inList) {
        html += "</ol>";
        inList = false;
      }
      const sectionTitle = trimmed.replace(/^[•●▪-]\s*/, "").replace(/:$/, "");
      html += `<p><strong>${sectionTitle}</strong></p>`;
    } else if (/^\d+\.\s/.test(trimmed)) {
      if (!inList) {
        html += "<ol>";
        inList = true;
      }
      const pointText = trimmed.replace(/^\d+\.\s*/, "");
      html += `<li>${pointText}</li>`;
    } else {
      if (inList) {
        html += "</ol>";
        inList = false;
      }
      html += `<p>${trimmed}</p>`;
    }
  }

  if (inList) {
    html += "</ol>";
  }

  return html;
};

const extractNamesFromHtml = (htmlString: string): string[] => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const names: string[] = [];

    doc.querySelectorAll("p").forEach((p) => {
      const text = p.textContent?.trim();
      if (text?.startsWith("Name:")) {
        const name = text.replace("Name:", "").trim();
        if (name) names.push(name);
      }
    });

    return names;
  } catch {
    return [];
  }
};

const AICompanion: React.FC<AICompanionProps> = ({
  onClose,
  aiSuggestion,
  loading,
  userData,
  aiContextHistory,
}) => {
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const getMatchedContextLabel = (name: string): string => {
    if (!aiContextHistory || aiContextHistory.length === 0) {
      return `AI Companion: ${name}`;
    }
    const matched = aiContextHistory.find((ctx: any) => {
      if (!ctx?.payload) return false;
      // Support old format with payload.context
      if (ctx.payload.context) {
        return ctx.payload.context.toLowerCase().includes(name.toLowerCase());
      }
      // New format: check contextName-specific value in payload
      const contextKey = ctx.contextName;
      const val = ctx.payload[contextKey];
      if (typeof val === "string") {
        return val.toLowerCase().includes(name.toLowerCase());
      }
      if (Array.isArray(val)) {
        return val.some((item: any) =>
          typeof item === "string"
            ? item.toLowerCase().includes(name.toLowerCase())
            : item?.organization?.toLowerCase().includes(name.toLowerCase()),
        );
      }
      return false;
    });

    if (matched) {
      const contextKey = matched.contextName;
      const val = matched.payload.context || matched.payload[contextKey];
      const displayVal = typeof val === "string"
        ? val
        : Array.isArray(val)
          ? val.map((v: any) => (typeof v === "string" ? v : v?.organization)).join(", ")
          : name;
      return formatContextLabel(matched.contextName, displayVal);
    }
    const latest = aiContextHistory[aiContextHistory.length - 1];
    return formatContextLabel(latest.contextName, name);
  };

  // Handle AI Suggestion update
  useEffect(() => {
    if (!aiSuggestion) return;

    if (typeof aiSuggestion === "object" && !Array.isArray(aiSuggestion) && aiSuggestion.sections) {
      const html = aiSuggestion.sections
        .map((section: any) => {
          const sectionHeader = `<p class="ai-section-title"><strong>${section.section}</strong></p>`;
          const items = (section.items || [])
            .map(
              (item: any) =>
                `<p class="ai-item-title"><strong>${item.title}</strong></p><ul><li>${item.insight}</li></ul>`,
            )
            .join("");
          return sectionHeader + items;
        })
        .join("");

      setConversation((prev) => [
        ...prev,
        {
          name: aiSuggestion.label || "AI Companion",
          keyPoints: [html],
          contextLabel: aiSuggestion.label || "AI Companion",
        },
      ]);
    } else if (typeof aiSuggestion === "string") {
      const extractedNames = extractNamesFromHtml(aiSuggestion);
      const cleanedHtml = aiSuggestion.replace(
        /<p><b>Name:<\/b>.*?<\/p>/gi,
        "",
      );
      const formattedHtml = formatPlainTextToHtml(cleanedHtml);

      if (extractedNames.length > 0) {
        setConversation((prev) => [
          ...prev,
          ...extractedNames.map((n) => ({
            name: n,
            keyPoints: [cleanedHtml],
            contextLabel: getMatchedContextLabel(n),
          })),
        ]);
      } else {
        setConversation((prev) => [
          ...prev,
          {
            name: "AI Companion",
            keyPoints: [cleanedHtml],
            contextLabel: getMatchedContextLabel("AI Companion"),
          },
        ]);
      }
    } else if (Array.isArray(aiSuggestion)) {
      setConversation((prev) => [
        ...prev,
        ...aiSuggestion.map((item: any) => ({
          ...item,
          contextLabel: getMatchedContextLabel(item.name),
        })),
      ]);
    }
  }, [aiSuggestion]);

  useEffect(() => {
    if (conversation?.length !== 0 && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [conversation]);

  return (
    <div className="ai-companion-card">
      {/* Header */}
      <div className="ai-header">
        <h3>
          <Gemini style={{ width: "25px", height: "25px" }} data-testid="gemini-icon" /> AI Companion
        </h3>
        <div className="header-actions" data-testid="minimize-wrapper">
          <Minimize2
            className="icon"
            onClick={onClose}
            data-testid="minimize-icon"
          />
        </div>
      </div>

      {/* Body */}
      <div className="ai-body">
        <h4 className="greeting">
          <PiHandWavingFill color="#FFD150" className="icon" /> Hello{" "}
          {userData?.name || "there"},
        </h4>
        <p style={{ textAlign: "left" }}>
          This space will provide intelligent suggestions, risk indicators, and
          actionable insights.
        </p>

        <div className="chatbot-messages" ref={chatContainerRef}>
          {conversation.map((item, idx) => (
            <div key={idx} className="chat-message">
              <div className="chat-sender">
                {item.contextLabel.startsWith("Auditee member")
                  ? item.name
                  : item.contextLabel}
              </div>

              <div className="chat-points">
                {(Array.isArray(item.keyPoints)
                  ? item.keyPoints
                  : [item.keyPoints]
                ).map(
                  (point, pIdx) =>
                    point && (
                      <div
                        key={pIdx}
                        className="chat-bubble blue"
                        dangerouslySetInnerHTML={{
                          __html:
                            typeof point === "string"
                              ? point
                              : JSON.stringify(point),
                        }}
                      />
                    ),
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "1rem",
                padding: "2rem",
              }}
              data-testid="loader-container"
            >
              <PulseLoader size={10} color="#4a4a4a" />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AICompanion;
